import React from 'react';
import { hydrateRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { applyMiddleware, combineReducers, createStore, compose as composeOrig } from 'redux';
import thunk from 'redux-thunk';
import { triggerWebpackBundle } from '@gumtree/shared/src/manifest-loader/static-js-files';
import * as Sentry from '@sentry/react';
import { GrowthBookProvider } from '@growthbook/growthbook-react';

import initAds from '@gumtree/third-party-ads/init-ads';
import trackConsentState from '@gumtree/third-party-ads/src/framework/gdpr/track-consent-state';
import trackingEventsOnOneTrust from '@gumtree/third-party-ads/src/framework/gdpr/tracking-events';
import { initPermutiveWithConsent } from '@gumtree/third-party-ads/src/framework/permutive/set-permutive-consent';
import { processPermutiveQueue } from '@gumtree/shared/src/util/permutive/queuing-system';
import iframeGaEventListener from '@gumtree/shared/src/partnerships/analytics/iframe-ga-event-listener';
import { adPerformanceTracker } from '@gumtree/third-party-ads/src/framework/tracking/ad-performance-tracker';

import 'normalize.css/normalize.css';
import '@gumtree/ui-library/src/base/base.scss';
import '@gumtree/ui-library/src/typography/typography.scss';
import { addBlockthrough } from '@gumtree/third-party-ads/src/framework/blockthrough/add-blockthrough';
import { GOOGLE_CLIENT_ID } from '@gumtree/ui-library/src/google-review/config';
import { isOneTapAllowed } from '@gumtree/shared/src/util/one-tap';

import { markDuckDuckGoCookie } from '@gumtree/shared/src/util/ad-block-util';
import { initWebVitalsMonitor } from '@gumtree/shared/src/performance/web-vitals-monitor';
import { syiPerformanceMonitor } from '@gumtree/shared/src/performance/syi-performance-monitor';
import createAppWith from './create-app';
import loadView from './load-view';
import initComScore from './com-score/init-com-score';

import logger from './logger';

import initGumtreeApi from './gumtree-api/init';
import qualtricsHelperInit from '../../syi/src/services/qualtrics-helper';
import { pageNames } from './route-map';
import initSentry from './sentry/init-sentry';
import { initForcedFeatures, initGrowthbook } from './growthbook/init-growthbook';
import { initBraze } from './braze/init-braze';
import initGoogleOneTapClient from './google-one-tap/init-google-one-tap';
import { initMonitor } from './monitor';

initMonitor();
triggerWebpackBundle();
/** decodes clientData after it was encoded by SSR */
window.clientData = JSON.parse(decodeURIComponent(window?.clientData));

// @ts-ignore
const { clientData: data, growthbookExperimentData, experimentOverrides } = window;

const { page, baseConfig, userData } = data;

const parsedExperimentData = JSON.parse(decodeURIComponent(growthbookExperimentData));
const growthbook = initGrowthbook(
    parsedExperimentData,
    page.appViewDevice,
    page.gbUserPseudoId,
    true
);
const isMobile = baseConfig.device?.type === 'mobile';
window.growthbook = growthbook;

// Toggle: enable client-side One Tap init here (keep SSR head variant as-is for A/B comparison)
const allowOneTap = isOneTapAllowed(page.type);
// Disable One Tap in Cypress integration tests to avoid UI inconsistencies
if (allowOneTap && !(window as any).Cypress) {
    initGoogleOneTapClient({
        clientId: baseConfig.google_app_id || GOOGLE_CLIENT_ID,
        isLoggedIn: Boolean(userData?.userLoggedIn),
        isMobile,
    });
}

const parsedExperimentOverrides = JSON.parse(decodeURIComponent(experimentOverrides));
initForcedFeatures(growthbook, parsedExperimentOverrides);
initSentry(baseConfig.isDev, baseConfig.environment, baseConfig.sentryDSN);
initGumtreeApi(window);
qualtricsHelperInit(window);
initPermutiveWithConsent(window);
processPermutiveQueue(window);

(window as any).adPerformanceTracker = adPerformanceTracker;
if ((window as any)._gptRequestStartTime) {
    // console.log('window._gptRequestStartTime===', (window as any)._gptRequestStartTime, performance.now())
    adPerformanceTracker.setGPTScriptRequestStartTime((window as any)._gptRequestStartTime);
} else {
    adPerformanceTracker.markGPTScriptRequestStart();
}

adPerformanceTracker.initGPTLoadTracking();

// @ts-ignore
const braze = initBraze({
    apiKey: baseConfig.brazeApiKey,
    baseUrl: baseConfig.brazeBaseUrl,
    enableLogging: baseConfig.isDev,
    user: userData,
});
braze && ((window as any).braze = braze);

initAds(window as any);
Math.random() < 0.001 && trackConsentState(window);
trackingEventsOnOneTrust(window);
iframeGaEventListener();

const composeEnhancers =
    (baseConfig.showReduxDevTools &&
        typeof window !== 'undefined' &&
        // @ts-ignore
        window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__) ||
    composeOrig;

initComScore(window);

initWebVitalsMonitor({
    pageType: baseConfig.pageType,
    deviceType: baseConfig.device?.type,
    clsThreshold: 0.01,
    inpThreshold: 200,
    enableDebugLog: false, // prod env off debug log
});

// Initialize SYI performance monitor for PostAd/EditAd pages only on mobile devices
if (['PostAd', 'EditAd'].includes(baseConfig.pageType) && page.appViewDevice) {
    syiPerformanceMonitor.start({
        pageType: baseConfig.pageType,
        appViewDevice: page.appViewDevice,
        categoryId: '',
    });
}

window.addEventListener('beforeinstallprompt', (event) => {
    event.preventDefault();
});

function unregisterServiceWorker() {
    if ('serviceWorker' in navigator) {
        navigator.serviceWorker
            .getRegistrations()
            .then(function (registrations) {
                for (const registration of registrations) {
                    registration.unregister();
                }
            })
            .catch(function (err) {
                logger.error('Service Worker registration failed: ', err);
            });
    }
}

// @ts-ignore
loadView(page.type).then(({ App, reducers }) => {
    page.App = App;

    const middlewares = [thunk];
    const store = createStore(
        combineReducers({ request: (state = {}) => state, ...reducers }),
        data,
        composeEnhancers(applyMiddleware(...middlewares))
    );

    unregisterServiceWorker();

    // Mark hydration start for SYI performance tracking
    syiPerformanceMonitor.markHydrationStart();

    try {
        hydrateRoot(
            document.querySelector('#content') as HTMLElement,
            // <CacheProvider value={cache}>
            <GrowthBookProvider growthbook={growthbook}>
                <BrowserRouter>{createAppWith(store)}</BrowserRouter>
            </GrowthBookProvider>
            // </CacheProvider>
        );

        // Mark hydration end and schedule performance report
        syiPerformanceMonitor.markHydrationEnd();
        syiPerformanceMonitor.scheduleReport();
    } catch (e) {
        Sentry.captureException(e);
        throw e;
    }

    if (typeof window !== 'undefined') {
        import('lazysizes').then((lazy) => lazy.init());
    }

    if (window.Cypress) {
        // @ts-ignore
        window.store = store;
    }

    markDuckDuckGoCookie(window);

    if (page.type === pageNames.SRP) {
        addBlockthrough(window);
    }
});
